import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type {
  OauthConsentRequestDto,
  OauthDecisionResultDto,
  OauthGrantListDto,
} from '@lib/dto/oauth-consent.dto'
import type { OauthConsentDecisionInput } from '@lib/input/oauth-consent.input'
import { oauthService } from '@/services/OauthService'
import { oauthKeys } from '@/hooks/queryKeys'

/**
 * Load the connection a link is asking about.
 *
 * `retry: false` because a missing or expired request is a final answer, and
 * retrying it only delays the screen that says so. The request does not change
 * while it is open, so it is not refetched.
 */
export function useOauthConsentRequest(
  requestId: string | null,
): UseQueryResult<OauthConsentRequestDto> {
  return useQuery({
    queryKey: oauthKeys.request(requestId ?? ''),
    queryFn: () => oauthService.getRequest(requestId ?? ''),
    enabled: requestId !== null && requestId !== '',
    retry: false,
    staleTime: Infinity,
  })
}

interface OauthDecisionVariables {
  requestId: string
  input: OauthConsentDecisionInput
}

/**
 * Approve or refuse a connection. Nothing is invalidated: on success the
 * browser leaves for the app, and the code exists only on that URL.
 */
export function useDecideOauthConsent(): UseMutationResult<
  OauthDecisionResultDto,
  Error,
  OauthDecisionVariables
> {
  return useMutation({
    mutationFn: ({ requestId, input }: OauthDecisionVariables) =>
      oauthService.decide(requestId, input),
  })
}

/** This person's live connected apps. */
export function useOauthGrants(): UseQueryResult<OauthGrantListDto> {
  return useQuery({
    queryKey: oauthKeys.grants(),
    queryFn: () => oauthService.listGrants(),
  })
}

/** Disconnect one app and refresh the list. */
export function useRevokeOauthGrant(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => oauthService.revokeGrant(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: oauthKeys.grants() })
    },
  })
}
